import { Button } from "@/components/ui/button";
import { ContentLoader } from "@/components/ui/loaders";
import { CampaignHero, GroupRow } from "@/components/fornada/campaign";
import {
  BarRow,
  EmptyState,
  KpiCard,
  ScreenTitle,
  SectionHeader,
  Surface,
} from "@/components/fornada/primitives";
import { useActiveSale } from "@/hooks/use-active-sale";
import { date } from "@/lib/date";
import { eyebrowDate, firstName, formatQuantity } from "@/lib/format";
import {
  byFlavor,
  isOrdersOpen,
  orderPositions,
  orderSummary,
  pizzasOf,
} from "@/lib/sale";
import { orderCode } from "@/lib/format";
import { useAuth } from "@/providers/auth.provider";
import { Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { sortBy } from "lodash";

const HomePage = () => {
  const { user } = useAuth();
  const { data: sale, isLoading } = useActiveSale();
  const isAdmin = user?.role === "ADMIN";

  if (isLoading) return <ContentLoader show />;

  const title = (
    <ScreenTitle
      eyebrow={eyebrowDate(date())}
      title={`Olá, ${firstName(user?.name)}`}
      subtitle="Acompanhe suas vendas e o prazo dos pedidos."
    />
  );

  if (!sale) {
    return (
      <div className="flex flex-col gap-7">
        {title}
        <EmptyState
          title="Nenhuma campanha ativa"
          description="Quando uma campanha for aberta, os pedidos aparecem aqui."
        >
          {isAdmin && (
            <Button asChild size="lg" className="mt-2">
              <Link to="/campaign/edit?new=1">
                <Plus /> Criar campanha
              </Link>
            </Button>
          )}
        </EmptyState>
      </div>
    );
  }

  const open = isOrdersOpen(sale);
  // Depois do prazo, o ADMIN ainda registra pedidos (inclusive para outro vendedor).
  const canOrder = open || isAdmin;
  const mine = sale.orders.filter((o) => o.userId === user?.id);
  const myPizzas = pizzasOf(mine);
  const today = date().startOf("date");
  const myToday = pizzasOf(
    mine.filter((o) => !date(o.createdAt as unknown as string).isBefore(today)),
  );
  const positions = orderPositions(sale.orders);
  const recent = sortBy(mine, (o) => -date(o.createdAt as unknown as string).valueOf()).slice(0, 3);
  const flavors = byFlavor(isAdmin ? sale.orders : mine);
  const top = flavors[0]?.value ?? 1;
  const sellers = new Set(sale.orders.map((o) => o.userId)).size;
  const myFlavorCount = new Set(mine.flatMap((o) => o.items.map((i) => i.flavorId))).size;

  return (
    <div className="flex flex-col gap-7">
      <ScreenTitle
        eyebrow={eyebrowDate(date())}
        title={`Olá, ${firstName(user?.name)}`}
        subtitle="Acompanhe suas vendas e o prazo dos pedidos."
      >
        {canOrder && (
          <Button asChild className="hidden min-[960px]:inline-flex">
            <Link to="/orders/new">
              <Plus strokeWidth={2.2} /> Novo pedido
            </Link>
          </Button>
        )}
      </ScreenTitle>

      <div className="grid gap-3 min-[960px]:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] min-[960px]:gap-4">
        <CampaignHero sale={sale} />

        <div className="grid grid-cols-2 gap-3">
          <KpiCard
            label="Minhas vendas"
            value={myPizzas}
            note={myToday > 0 ? `+${myToday} hoje` : undefined}
            noteAccent
          />
          {isAdmin ? (
            <KpiCard
              label="Total campanha"
              value={pizzasOf(sale.orders)}
              note={`${sale.orders.length} pedidos`}
            />
          ) : (
            <KpiCard
              label="Meus pedidos"
              value={mine.length}
              note={`${myFlavorCount} ${myFlavorCount === 1 ? "sabor" : "sabores"}`}
            />
          )}
          {isAdmin && (
            <>
              <div className="hidden min-[960px]:contents">
                <KpiCard
                  label="Pedidos"
                  value={sale.orders.length}
                  note={`${mine.length} seus`}
                />
                <KpiCard label="Vendedores" value={sellers} />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid gap-7 min-[960px]:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] min-[960px]:gap-4">
        <section className="flex flex-col gap-3">
          <SectionHeader title="Meus pedidos" action="Ver todos" to="/orders" />
          {recent.length > 0 ? (
            <Surface className="overflow-hidden">
              {recent.map((order) => {
                const created = date(order.createdAt as unknown as string);
                const when = created.isSame(today, "day")
                  ? `hoje, ${created.format("HH:mm")}`
                  : created.isSame(today.subtract(1, "day"), "day")
                    ? "ontem"
                    : created.format("DD/MM");
                return (
                  <GroupRow
                    key={order.id}
                    title={orderSummary(order)}
                    meta={`Pedido ${orderCode(positions.get(order.id) ?? 0)} · ${when}`}
                    value={order.items.reduce((n, i) => n + i.quantity, 0)}
                  />
                );
              })}
            </Surface>
          ) : (
            <EmptyState
              title="Nenhum pedido ainda"
              description={
                open
                  ? "Registre o primeiro pedido da campanha."
                  : "Os pedidos desta campanha estão fechados."
              }
              className="py-8"
            />
          )}
        </section>

        {flavors.length > 0 && (
          <section className="hidden flex-col gap-3 min-[960px]:flex">
            <SectionHeader
              title="Por sabor"
              action={isAdmin ? "Detalhes" : undefined}
              to="/campaign"
            />
            <Surface className="px-[18px] py-1.5">
              {flavors.slice(0, 5).map((f) => (
                <BarRow
                  key={f.key}
                  name={f.name}
                  value={formatQuantity(f.value)}
                  percent={Math.round((f.value / top) * 100)}
                />
              ))}
            </Surface>
          </section>
        )}
      </div>

      {canOrder && (
        <Link
          to="/orders/new"
          className="fixed right-5 bottom-[80px] z-12 flex h-14 items-center gap-2 rounded-full bg-fx-cream pr-6 pl-5 text-[15px] font-bold text-fx-on-cream shadow-[0_10px_30px_rgba(0,0,0,0.7)] transition-transform duration-[180ms] ease-fx active:scale-[0.97] min-[960px]:hidden"
        >
          <Plus size={20} strokeWidth={2.2} /> Novo pedido
        </Link>
      )}
    </div>
  );
};

export default HomePage;
